<script lang="ts" setup>
import {
  Cell,
  CellGroup,
  Popup,
  showDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
  Uploader,
} from "vant";
import { IMAGE_ACCEPT, VIDEO_ACCEPT } from "@/modules/constant";
import { computed, nextTick, ref, Ref, UnwrapRef, watch } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import { useMedia } from "@/stores/media";
import { useMate } from "@/stores/mate";
import { useTask } from "@/stores/task";
import { storeToRefs } from "pinia";
import { MateGalleryInstance } from "@/components/mobile/media/MateGallery.vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import GenGallery, {
  GenGalleryInstance,
  GenMedia,
} from "@/components/mobile/media/GenGallery.vue";
import { isGifMedia, useRawImageUrl } from "@/use/common";
import ProfileIcon from "@/components/icon/ProfileIcon.vue";
import BookIcon from "@/components/icon/BookIcon.vue";
import StoryGallery, {
  StoryGalleryInstance,
} from "@/components/mobile/media/StoryGallery.vue";
import CropImageOverlay, {
  CropImageOverlayInstance,
} from "@/components/mobile/media/CropImageOverlay.vue";
import i18n from "@/locales";
import { useUI } from "@/stores/ui";
import { Mate, MediaType, ModuleEntityType } from "@/types/entity";
import { ModuleGalleryInstance } from "./ModuleGallery.vue";
import api from "@/modules/api";
import { useRouter } from "vue-router";
// Props & Emit

export interface MediaSelectPopupProps {
  show: boolean;
  mediaType: "img" | "video";
  mediaList?: MediaUploadFileItem[];
  multiple: boolean;
  uploadType: MediaType;
  enableImageGenGallery?: boolean;
  enableMateGallery?: boolean;
  enableAvatarGallery?: boolean;
  enableModuleGallery?: boolean;
  enableGoImageGen?: boolean;
  enableUseAvatar?: boolean;
  enableMateWish?: boolean;
  enableDraw?: boolean;
  enableCrop?: boolean;
  storyId?: number; // 传storyId则启用StoryGallery
  maxCount?: number;
  replace?: boolean; // true-替换mediaList，false-追加mediaList
  cropAspectRatio?: number;
  showRatioSelect?: boolean;
  enablePortraitGallery?: boolean; // 是否启用立绘画廊
  isCropGif?: boolean; // 是否裁剪gif
  allowNsfw?: boolean; // 是否允许选择NSFW图片
  allowCropOutside?: boolean; // 是否允许裁剪超出图片范围的部分
  mode?: number;
  mateId?: number;
  entityId?: number;
  entityType?: ModuleEntityType;
  needUpload?: boolean; // 是否需要上传
}

const props = withDefaults(defineProps<MediaSelectPopupProps>(), {
  mediaList: () => [],
  enableUseAvatar: false,
  enableMateGallery: true,
  enableImageGenGallery: true,
  enableModuleGallery: false,
  enableAvatarGallery: false,
  enableDraw: false,
  enableCrop: false,
  maxCount: 1,
  replace: false,
  enableGoImageGen: true,
  cropAspectRatio: 3 / 4,
  enablePortraitGallery: false,
  isCropGif: false,
  allowNsfw: false,
  allowCropOutside: true,
  mode: 2,
  needUpload: true,
});
const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "update:mediaList", mediaList: MediaUploadFileItem[]): void;
  (e: "onMediaSelected", mediaList: MediaUploadFileItem[]): void; // 图片选择完成&&上传完成回调用
  (e: "update:cropAspectRatio", ratio: "3:4" | "16:9"): void;
}>();
// 内部状态
const router = useRouter();
const mateStore = useMate();
const taskStore = useTask();
const { currentMate } = storeToRefs(mateStore);
const mediaStore = useMedia();
const accept = computed(() =>
  props.mediaType === "img" ? IMAGE_ACCEPT : VIDEO_ACCEPT
);
const maxGallerySelectCount = computed(() => {
  if (props.replace) {
    return props.maxCount;
  } else {
    return props.maxCount - props.mediaList.length;
  }
});
const showGallery = ref(false);
const galleryRef = ref<MateGalleryInstance | null>(null);
const showGenGallery = ref(false);
const genGalleryRef = ref<GenGalleryInstance | null>(null);
const showStoryGallery = ref(false);
const storyGalleryRef = ref<StoryGalleryInstance | null>(null);
// const showMateSelect = ref(false);
const showPortraitGallery = ref(false);
const portraitGalleryRef = ref<GenGalleryInstance | null>(null);
const showModuleGallery = ref(false);
const moduleGalleryRef = ref<ModuleGalleryInstance | null>(null);

const showGalleryTypePopup = computed(() => {
  return !(
    showGallery.value ||
    showGenGallery.value ||
    showStoryGallery.value ||
    showPortraitGallery.value ||
    showModuleGallery.value
  );
});
// Lifecycle
watch(
  () => props.show,
  (val) => {
    if (!val) {
      showGallery.value = false;
      showGenGallery.value = false;
      showStoryGallery.value = false;
      showPortraitGallery.value = false;
      showModuleGallery.value = false;
      galleryRef.value?.selectedMediaList.forEach(
        (item) => (item.selected = false)
      );
      genGalleryRef.value?.clear();
      storyGalleryRef.value?.clear();
      portraitGalleryRef.value?.clear();
    }
  }
);
// Event Handler
const shouldCrop = (file: MediaUploadFileItem) =>
  props.enableCrop && props.maxCount === 1 && !isGifMedia(file);
/** 从浏览器上传 */
const onMediaLoaded = async (
  file: MediaUploadFileItem | MediaUploadFileItem[]
) => {
  const item = file as MediaUploadFileItem;
  if (shouldCrop(item) && !item.file?.name.includes(".webp")) {
    cropper.value?.showCropper(item.content!);
    tmpCropperImg = item;
    return;
  } else {
    await onMediaUpload(file);
    emit("update:show", false);
  }
};
const onMediaUpload = async (
  file: MediaUploadFileItem | MediaUploadFileItem[]
) => {
  const toast = showLoadingToast({
    duration: 0,
    message: i18n.global.t("Message.uploading"),
  });
  try {
    let mediaList = Array.isArray(file) ? file : [file];
    if (props.needUpload) {
      mediaList = await mediaStore.uploadMedia(
        file,
        currentMate.value!.id!,
        props.entityType === "mate" ? 5 : props.uploadType
      );

      if (props.entityId && props.entityType && props.entityType !== "mate") {
        await api.createOneModuleMedia({
          entityId: props.entityId!,
          entityType: props.entityType!,
          mediaId: mediaList[0].mediaId!,
          type: 1,
        });
      }
    }

    let newMediaList = [] as MediaUploadFileItem[];
    if (props.replace) {
      newMediaList = mediaList;
    } else {
      newMediaList = [...props.mediaList].slice(0, props.maxCount);
    }
    if (props.enableCrop && props.maxCount === 1) {
      emit("update:mediaList", []);
      nextTick(() => {
        emit("update:mediaList", newMediaList);
        emit("onMediaSelected", newMediaList);
        tmpCropperImg = null;
      });
    }
    toast.close();
    showSuccessToast({
      message: i18n.global.t("Message.uploadSuccessful"),
      onClose: () => {
        if (!(props.enableCrop && props.maxCount === 1)) {
          emit("update:mediaList", newMediaList);
          emit("onMediaSelected", newMediaList);
        }
      },
    });
  } catch (e) {
    showFailToast(i18n.global.t("Message.uploadFailed"));
    throw e;
  } finally {
    toast.close();
  }
};
const onUpload = (list: MediaUploadFileItem[]) => {
  const newList = props.replace ? list.slice(-props.maxCount) : list;
  emit("update:mediaList", newList);
};

let tmpCropperImg: MediaUploadFileItem | null = null;

const onConfirmGallerySelect = (
  ref:
    | UnwrapRef<MateGalleryInstance>
    | UnwrapRef<GenGalleryInstance>
    | UnwrapRef<StoryGalleryInstance>
    | UnwrapRef<ModuleGalleryInstance>
) => {
  // 1. 获取选中的media列表，并转换为标准格式
  const selectedMediaList = ref.selectedMediaList as GenMedia[];
  const mediaList: MediaUploadFileItem[] = selectedMediaList.map((item) => {
    return {
      ...item,
      mediaId: item.id,
      isImage: true,
      status: "done",
      videoCoverUrl: item.videoCoverUrl ?? "",
    } as MediaUploadFileItem;
  });
  // 2.1 需要裁剪
  if (props.enableCrop && props.maxCount === 1) {
    tmpCropperImg = mediaList[0];
    cropper.value?.showCropper(useRawImageUrl(mediaList[0].url!));
  } else {
    // 2.2 不需要裁剪
    let newMediaList = [];
    if (props.replace) {
      newMediaList = mediaList;
    } else {
      newMediaList = [...props.mediaList, ...mediaList].slice(
        0,
        props.maxCount
      );
    }
    emit("update:mediaList", newMediaList);
    emit("onMediaSelected", newMediaList);
    emit("update:show", false);
  }
};
const onUseAvatar = () => {
  const mediaItem = {
    url: mateStore.currentMate!.mediaUrl,
    mediaId: mateStore.currentMate!.mediaId,
    isImage: true,
    status: "done",
  } as MediaUploadFileItem;
  let newMediaList: MediaUploadFileItem[] = [];
  newMediaList = [mediaItem];
  // if (props.replace) {
  //   newMediaList = [mediaItem];
  // } else {
  //   newMediaList = [...props.mediaList, mediaItem].slice(0, props.maxCount);
  // }
  emit("update:mediaList", newMediaList);
  emit("onMediaSelected", newMediaList);
  emit("update:show", false);
};

// Cropper
const cropper = ref<CropImageOverlayInstance | null>(null);

const afterCrop = async (base64: string, file: File) => {
  const originalMedia = tmpCropperImg!;
  Object.assign(originalMedia, {
    url: base64,
    content: base64,
    status: "uploading",
    file,
  });
  await onMediaUpload(originalMedia);
  emit("update:show", false);
};

const onCropAspectRatioChange = (ratio: "3:4" | "16:9") => {
  emit("update:cropAspectRatio", ratio);
};

export interface MediaSelectPopupInstance {
  showGenGallery: Ref<boolean>;
  showGallery: Ref<boolean>;
}

defineExpose<MediaSelectPopupInstance>({
  showGenGallery,
  showGallery,
});

const uiStore = useUI();
const updateGalleryMate = (mateId: number) => {
  mateStore.changeGalleryCurrentMate(mateId);
  taskStore.galleryList = {
    1: [] as number[],
    2: [] as number[],
  };
  taskStore.galleryMap.clear();
  genGalleryRef.value?.clear();
};

const onGalleryMateUpdate = (mateId: number) => {
  if (genGalleryRef.value!.selectedMediaList.length! === 0)
    return updateGalleryMate(mateId);
  showDialog({
    title: i18n.global.t("ConfirmModal.promptTitle"),
    message: i18n.global.t("ConfirmModal.switchCharacterImageInfo"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
    showCancelButton: true,
  })
    .then(() => {
      updateGalleryMate(mateId);
    })
    .catch(() => {});
};

const onCropCancel = () => {
  emit("update:mediaList", []);
  emit("update:show", false);
};

const showMateSelector = ref(false);

const onConfirmMateSelect = (mateList: Mate[]) => {
  emit(
    "onMediaSelected",
    mateList.map((mate) => ({
      url: mate.mediaUrl,
      mediaId: mate.mediaId,
      isImage: true,
      status: "done",
    }))
  );
};
</script>
<template>
  <popup
    :class="{
      'h-[90%]': !showGalleryTypePopup,
    }"
    :show="show"
    position="bottom"
    round
    teleport="body"
    @close="showGallery = false"
    @update:show="emit('update:show', $event)"
  >
    <div v-if="showGalleryTypePopup" class="py-4 text-base flex flex-col gap-2">
      <!-- <cell-group inset>
        <cell
          v-if="enableImageGenGallery && enableGoImageGen"
          :title="
            $t(mode === 10 ? 'Cell.useAiDrawingEmoji' : 'Cell.useAiDrawing')
          "
          is-link
          @click="router.push({ name: 'imagePaintWithMode', query: { mode } })"
        />
      </cell-group> -->
      <cell-group inset>
        <VanCell
          v-if="enableMateWish"
          @click="router.push({ name: 'mateWishEdit' })"
          is-link
        >
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconify mingcute--magic-2-line" />
              <div>{{ $t("mateWish.name") }}</div>
            </div>
          </template>
        </VanCell>
        <VanCell
          v-if="enableDraw"
          @click="router.push({ name: 'draw' })"
          is-link
        >
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconify mingcute--paint-brush-line" />
              <div>{{ $t("Buttons.handDraw") }}</div>
            </div>
          </template>
        </VanCell>
        <uploader
          :accept="accept"
          :after-read="onMediaLoaded"
          :max-count="maxCount"
          :model-value="mediaList"
          :multiple="multiple"
          :preview-image="false"
          :reupload="replace"
          class="gallery-uploader w-full"
          @update:modelValue="onUpload($event)"
        >
          <cell>
            <template #title>
              <div class="flex items-center gap-2">
                <!-- <upload-icon class="icon-sm" /> -->
                <i class="iconify mingcute--file-upload-line" />
                <div>
                  {{ $t("Cell.upload")
                  }}{{
                    props.mediaType === "img"
                      ? $t("Cell.image")
                      : $t("Cell.video")
                  }}
                </div>
              </div>
            </template>
          </cell>
        </uploader>
        <cell v-if="enableMateGallery" is-link @click="showGallery = true">
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconify mingcute--pic-ai-line"></i>
              <div>{{ $t("Cell.characterSelect") }}</div>
            </div>
          </template>
        </cell>
        <cell
          v-if="enableAvatarGallery"
          is-link
          @click="showMateSelector = true"
        >
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconify mingcute--user-2-line"></i>
              <div>{{ $t("Cell.avatarSelect") }}</div>
            </div>
          </template>
        </cell>
        <cell
          v-if="enableModuleGallery"
          is-link
          @click="showModuleGallery = true"
        >
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconify mingcute--pic-ai-line"></i>
              <div>{{ $t("Cell.worldviewSelect") }}</div>
            </div>
          </template>
        </cell>
        <!-- <cell
          v-if="enableImageGenGallery"
          is-link
          @click="showGenGallery = true"
        >
          <template #title>
            <div class="flex items-center gap-2">
              <magic-stick-icon class="icon-sm" />
              <div>{{ $t("Cell.generatedImage") }}</div>
            </div>
          </template>
        </cell> -->
        <cell v-if="storyId" is-link @click="showStoryGallery = true">
          <template #title>
            <div class="flex items-center gap-2">
              <book-icon />
              <div>{{ $t("Cell.chapterSelect") }}</div>
            </div>
          </template>
        </cell>
        <cell
          v-if="enablePortraitGallery"
          is-link
          @click="showPortraitGallery = true"
        >
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconfont icon-image-portrait" />
              <div>{{ $t("Cell.portraitsSelect") }}</div>
            </div>
          </template>
        </cell>
        <cell
          v-if="enableUseAvatar && currentMate!.mediaUrl"
          clickable
          @click="onUseAvatar"
        >
          <template #title>
            <div class="flex items-center gap-2">
              <profile-icon class="icon-sm" />
              <div>{{ $t("Cell.useAvatar") }}</div>
            </div>
          </template>
        </cell>
      </cell-group>
      <cell-group inset>
        <cell
          :title="$t('Cell.cancel')"
          clickable
          @click="emit('update:show', false)"
        />
      </cell-group>
    </div>
    <div
      v-if="showGallery"
      class="grid grid-rows-[auto_1fr] overflow-hidden h-full"
    >
      <div class="navbar bg-base-100 h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button class="btn btn-ghost" @click="showGallery = false">
            <light-arrow-icon />
          </button>
        </div>
        <div class="navbar-center">
          <div>{{ $t("Cell.characterAlbum") }}</div>
        </div>
        <div class="navbar-end">
          <button
            :disabled="galleryRef?.selectedMediaList.length === 0"
            class="btn btn-primary btn-sm rounded"
            @click="onConfirmGallerySelect(galleryRef!)"
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </div>
      </div>
      <div class="h-full overflow-y-auto">
        <mate-gallery
          ref="galleryRef"
          :filter="mediaType === 'img' ? 1 : 2"
          :max-select-count="maxGallerySelectCount"
          selectable
        />
      </div>
    </div>

    <div
      v-if="showModuleGallery"
      class="grid grid-rows-[auto_1fr] overflow-hidden h-full"
    >
      <div class="navbar bg-base-100 h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button class="btn btn-ghost" @click="showModuleGallery = false">
            <light-arrow-icon />
          </button>
        </div>
        <div class="navbar-center">
          <div>{{ $t("Cell.worldviewAlbum") }}</div>
        </div>
        <div class="navbar-end">
          <button
            :disabled="moduleGalleryRef?.selectedMediaList.length === 0"
            class="btn btn-primary btn-sm rounded"
            @click="onConfirmGallerySelect(moduleGalleryRef!)"
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </div>
      </div>
      <div class="h-full overflow-y-auto">
        <module-gallery
          ref="moduleGalleryRef"
          :entity-id="entityId!"
          :entity-type="entityType!"
          :filter="mediaType === 'img' ? 1 : 2"
          :max-select-count="maxGallerySelectCount"
          selectable
        />
      </div>
    </div>
    <div
      v-if="showGenGallery"
      class="grid grid-rows-[auto_1fr] overflow-hidden h-full"
    >
      <div class="navbar bg-base-100 h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button class="btn btn-ghost" @click="showGenGallery = false">
            <light-arrow-icon />
          </button>
        </div>
        <div
          class="navbar-center cursor-pointer"
          @click="
            uiStore.popMateSelector({
              callback: (mate) => {
                onGalleryMateUpdate(mate.id);
                uiStore.showMateSelector = false;
              },
              switchMate: false,
            })
          "
        >
          <div>
            {{
              mateStore.galleryCurrentMate
                ? mateStore.galleryCurrentMate.name
                : mateStore.currentMate?.name
            }}
            <i class="iconfont icon-down1 text-sm"></i>
          </div>
        </div>
        <div class="navbar-end flex gap-1">
          <!-- <button
            class="btn btn-primary btn-xs rounded"
            @click="
              router.push({ name: 'imageGeneration', query: { mode: 2 } })
            "
          >
            {{ $t("Cell.aiDrawing") }}
          </button> -->
          <button
            :disabled="genGalleryRef?.selectedMediaList.length === 0"
            class="btn btn-primary btn-xs rounded"
            @click="onConfirmGallerySelect(genGalleryRef!)"
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </div>
      </div>
      <div class="h-full overflow-hidden">
        <gen-gallery
          ref="genGalleryRef"
          :filter="mediaType === 'img' ? 1 : 2"
          :max-select-count="maxGallerySelectCount"
          selectable
        />
      </div>
    </div>
    <div
      v-if="storyId && showStoryGallery"
      class="grid grid-rows-[auto_1fr] overflow-hidden h-full"
    >
      <div class="navbar bg-base-100 h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button class="btn btn-ghost" @click="showStoryGallery = false">
            <light-arrow-icon />
          </button>
        </div>
        <div class="navbar-center">
          <div>{{ $t("Cell.chapterAlbum") }}</div>
        </div>
        <div class="navbar-end flex gap-1">
          <button
            :disabled="storyGalleryRef?.selectedMediaList.length === 0"
            class="btn btn-primary btn-xs rounded"
            @click="onConfirmGallerySelect(storyGalleryRef!)"
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </div>
      </div>
      <div class="h-full overflow-y-auto">
        <story-gallery
          ref="storyGalleryRef"
          :filter="mediaType === 'img' ? 1 : 2"
          :max-select-count="maxGallerySelectCount"
          :story-id="storyId!"
          selectable
        />
      </div>
    </div>
    <portrait-gallery
      v-if="showPortraitGallery"
      ref="portraitGalleryRef"
      :filter="mediaType === 'img' ? 1 : 2"
      :max-select-count="maxGallerySelectCount"
      selectable
      @back="showPortraitGallery = false"
      @on-confirm="onConfirmGallerySelect(portraitGalleryRef!)"
    />
    <crop-image-overlay
      v-if="enableCrop"
      ref="cropper"
      :allowCropOutside="allowCropOutside"
      :crop-aspect-ratio="cropAspectRatio"
      :is-crop-gif="isCropGif"
      :on-finished="afterCrop"
      :showRatioSelect="showRatioSelect"
      @update:crop-aspect-ratio="onCropAspectRatioChange"
      @on-cancel="onCropCancel"
    />

    <ModalMutiMateSelect
      v-model:show="showMateSelector"
      only-has-avatar
      @confirm="onConfirmMateSelect"
    />
  </popup>
</template>
<style>
.gallery-uploader .van-uploader__input-wrapper {
  display: block !important;
}
</style>
